import React, { FC, Ref, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { IdNameList } from "../../../../api/services/common/models";
import {
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiMultiSelect,
  MuiRadioGroup,
  MuiSelect,
  MuiTextField,
  MuiTypography,
} from "../../../../components";
import MeasurementInput from "../../../../components/measurementInput/MeasurementInput";
import { MAX_LENGTH_75 } from "../../../../helpers/constants";
import { UnitTypesKey } from "../../../../helpers/constants/siteCoreKeys";
import {
  isDuplicateTopic,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  selectedUnitSelector,
  topicListSelector,
} from "../../../../redux/slices/mdmActivity/mdmActivitySelector";
import { AddTopicFormKey, topicTypeKey } from "../../modelsAndSchemas/models";
import { LocaleKey } from "../../modelsAndSchemas/ValidationSchema";
import { addTopicSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

interface MdmTopicDetailsProps {
  pageId: string;
  deliveryRef: Ref<{ resetValues: () => void }>;
  participantStatusRef: Ref<{ resetValues: () => void }>;
  existingDeliverablesData: IdNameList[];
  existingTopicsData: IdNameList[];
  selectedTopicType: string;
  setSelectedTopicType: React.Dispatch<React.SetStateAction<string>>;
  isEditTopic?: boolean;
  topicId: string;
}
export const MdmTopicDetails: FC<MdmTopicDetailsProps> = ({
  pageId,
  deliveryRef,
  participantStatusRef,
  existingDeliverablesData,
  existingTopicsData,
  selectedTopicType,
  setSelectedTopicType,
  isEditTopic = false,
  topicId,
}) => {
  const selectedUnit = useSelector(selectedUnitSelector);

  const { control, resetField, setValue, getValues, setError } =
    useFormContext();

  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const topicList = useSelector(topicListSelector);

  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const {
    addTopic,
    selectTypeSubHeader,
    existingTopicRadioButton,
    newTopicRadioButton,
    topicLabel,
    deliverablesLabel,
    minutesList,
    minutesLabel,
    quantityLabel,
    numberOfDaysLabel,
    activityStatusList,
    activityStatusLabel,
    participantStatusList,
    participantStatusLabel,
    selectPreferencesSubHeader,
    triggerBilling,
    triggerTouchPointSurvey,
    countTowardsLastContactDate,
    duplicateTopicErrorMessage,
  } = addTopicSiteCoreKeys;

  const getAddTopicLabel = (key: string) => {
    return fetchLabel(addTopic, key);
  };

  const radioOptions = [
    {
      value: topicTypeKey.existingTopic,
      label: getAddTopicLabel(existingTopicRadioButton),
    },
    {
      value: topicTypeKey.newTopic,
      label: getAddTopicLabel(newTopicRadioButton),
    },
  ];

  const handleTopicTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedType = e.target.value;
    setSelectedTopicType(selectedType);
    setValue(AddTopicFormKey.topicType, selectedType);
    resetField(AddTopicFormKey.topic);

    // Reset all locale fields
    const localeKeys: LocaleKey[] = [
      "en-US",
      "en-GB",
      "fr-FR",
      "pt-BR",
      "ja-JP",
    ];
    for (const key of localeKeys) {
      resetField(key);
    }
  };

  const checkDuplicationForTopic = () => {
    const topicValue = getValues(AddTopicFormKey.topic);

    const isDuplicate = isDuplicateTopic(
      topicList,
      topicValue,
      isEditTopic ? topicId : undefined,
    );

    if (isDuplicate) {
      setTimeout(() => {
        setError(AddTopicFormKey.topic, {
          type: "manual",
          message: getAddTopicLabel(duplicateTopicErrorMessage),
        });
      }, 0);
    }
  };

  const filteredExistingTopics = useMemo(() => {
    return existingTopicsData?.filter(
      (existing) =>
        !topicList.some(
          (topic) =>
            topic.topic === existing.name &&
            topic.topicType === selectedTopicType,
        ),
    );
  }, [existingTopicsData, topicList, selectedTopicType]);

  return (
    <>
      <MuiBox
        className="topic-container"
        data-testid={`${pageId}-topic-container`}
      >
        {!isEditTopic && (
          <MuiRadioGroup
            label={getAddTopicLabel(selectTypeSubHeader)}
            value={selectedTopicType}
            onChange={handleTopicTypeChange}
            options={radioOptions}
            wrapperClassName="topic-type-radio-group"
            requiredARIALabel={commonARIALabel.required}
          />
        )}

        <MuiGrid container className="form-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController control={control} name={AddTopicFormKey.topic}>
              {selectedTopicType === topicTypeKey.existingTopic ? (
                <MuiSelect
                  label={getAddTopicLabel(topicLabel)}
                  options={setApiDataToDropDownOption(filteredExistingTopics)}
                  visibleDefaultSelectOption
                  showSearch
                  value=""
                  id={`${pageId}-${AddTopicFormKey.topic}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                />
              ) : (
                <MuiTextField
                  label={getAddTopicLabel(topicLabel)}
                  id={`${pageId}-${AddTopicFormKey.topic}`}
                  isMandatory
                  placeholder={commonLabels.inputPlaceHolder}
                  maxLength={MAX_LENGTH_75}
                  showCloseIcon
                  onBlur={checkDuplicationForTopic}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              )}
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={AddTopicFormKey.deliverables}
              ref={deliveryRef}
            >
              <MuiMultiSelect
                label={getAddTopicLabel(deliverablesLabel)}
                options={setApiDataToDropDownOption(existingDeliverablesData)}
                showSearch
                showChip
                id={`${pageId}-${AddTopicFormKey.deliverables}`}
                chipId={`${pageId}-${AddTopicFormKey.deliverables}-chip`}
                dialogTitle={getAddTopicLabel(deliverablesLabel)}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
              />
            </FormController>
          </MuiGrid>

          {selectedUnit !== UnitTypesKey.HalfDay && (
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MeasurementInput
                selectedUnit={selectedUnit}
                measurementOtherMinuteName={AddTopicFormKey.measurementMinute}
                measurementOther={AddTopicFormKey.measurementOther}
                control={control}
                pageId={pageId}
                parentKey={addTopic}
                minutesLabelKey={minutesLabel}
                quantityLabelKey={quantityLabel}
                daysLabelKey={numberOfDaysLabel}
                minuteListKey={minutesList}
              />
            </MuiGrid>
          )}

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={AddTopicFormKey.activityStatus}
            >
              <MuiSelect
                label={getAddTopicLabel(activityStatusLabel)}
                options={fetchDropdownValues(addTopic, activityStatusList)}
                value=""
                id={`${pageId}-${AddTopicFormKey.activityStatus}`}
                placeholder={commonLabels.selectPlaceHolder}
                visibleDefaultSelectOption
                isMandatory
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={AddTopicFormKey.participantStatus}
              ref={participantStatusRef}
            >
              <MuiMultiSelect
                label={getAddTopicLabel(participantStatusLabel)}
                options={fetchDropdownValues(addTopic, participantStatusList)}
                showSearch
                showChip
                id={`${pageId}-${AddTopicFormKey.participantStatus}`}
                placeholder={commonLabels.selectPlaceHolder}
                isMandatory
                chipId={`${pageId}-${AddTopicFormKey.participantStatus}-chip`}
                dialogTitle={getAddTopicLabel(participantStatusLabel)}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      {/*  */}
      <MuiBox className="dashed-line" />
      {/*  */}
      <MuiBox className="preferences-container">
        <MuiBox>
          <MuiTypography
            id="id-group-label"
            component={"h3"}
            className="select-preference"
            variant={"h3Bold"}
          >
            {getAddTopicLabel(selectPreferencesSubHeader)}
          </MuiTypography>
        </MuiBox>
        <MuiBox
          role="group"
          aria-labelledby="id-group-label"
          className="checkbox-container"
        >
          <MuiGrid role="list" container className="form-container">
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              role="listitem"
            >
              <FormControllerCheckbox
                control={control}
                name={AddTopicFormKey.triggerBilling}
              >
                <MuiCheckbox
                  checkboxLabel={getAddTopicLabel(triggerBilling)}
                  id={`${pageId}-${AddTopicFormKey.triggerBilling}`}
                  data-testid={`${pageId}-${AddTopicFormKey.triggerBilling}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              role="listitem"
            >
              <FormControllerCheckbox
                control={control}
                name={AddTopicFormKey.lastContactDate}
              >
                <MuiCheckbox
                  checkboxLabel={getAddTopicLabel(countTowardsLastContactDate)}
                  id={`${pageId}-${AddTopicFormKey.lastContactDate}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              role="listitem"
            >
              <FormControllerCheckbox
                control={control}
                name={AddTopicFormKey.triggerTouch}
              >
                <MuiCheckbox
                  checkboxLabel={getAddTopicLabel(triggerTouchPointSurvey)}
                  id={`${pageId}-${AddTopicFormKey.triggerTouch}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      </MuiBox>
    </>
  );
};
